/**
 * Record presentation primitives — the visual vocabulary shared by every record
 * surface (form entries, calls, and anything added later).
 *
 * These encode the One Up Admin look so it is inherited rather than
 * reimplemented: initial avatars, outlined pills, uppercase section headings, a
 * bordered spec box for metadata, and striped field rows. Portals get a
 * consistent feel because they compose the same pieces, not because each one
 * copies the same Tailwind strings.
 *
 * Every colour is a semantic token (primary, muted, border), so Cascade's maroon
 * and One Up's admin theme both render correctly with no per-portal overrides.
 */

import type { ReactNode } from 'react';
import { ExternalLink, Mail, Phone } from 'lucide-react';

/* ------------------------------------------------------------------- identity */

/**
 * Initial avatar. Gives a list a scannable left edge and a consistent anchor
 * point — the eye lands on the same x-position for every row.
 */
export function RecordAvatar({
  name,
  size = 'md',
  icon,
}: {
  name?: string;
  size?: 'sm' | 'md';
  icon?: ReactNode;
}) {
  const initial = name?.trim()?.[0]?.toUpperCase();
  const dimensions = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-lg bg-emphasis font-semibold text-emphasis-foreground ${dimensions}`}
    >
      {icon ?? initial ?? '·'}
    </span>
  );
}

/** Outlined pill. Used for provenance — which form, which site, which agent. */
export function RecordPill({
  children,
  tone = 'outline',
}: {
  children: ReactNode;
  tone?: 'outline' | 'primary' | 'muted';
}) {
  const styles = {
    outline: 'border-border text-foreground',
    primary: 'border-primary/30 bg-emphasis text-emphasis-foreground',
    muted: 'border-transparent bg-muted text-muted-foreground',
  }[tone];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4 ${styles}`}>
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------- headings */

/**
 * Uppercase, letterspaced, quiet. Section headings should organise without
 * competing with the content beneath them.
 */
export function SectionHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {children}
      </h3>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------- spec box */

export interface SpecItem {
  label: string;
  value?: ReactNode;
}

/**
 * Bordered metadata block. Collects the "what/when/where" facts into one framed
 * unit so they read as context rather than as more content.
 */
export function SpecBox({ items, columns = 2 }: { items: SpecItem[]; columns?: 2 | 3 }) {
  const visible = items.filter((item) => item.value !== undefined && item.value !== null && item.value !== '');
  if (visible.length === 0) return null;
  return (
    <dl className={`grid gap-x-6 gap-y-4 rounded-lg border bg-card p-4 ${
      columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
    }`}>
      {visible.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {item.label}
          </dt>
          <dd className="mt-1 break-words text-sm">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ----------------------------------------------------------------- field rows */

export interface FieldRow {
  label: string;
  value: ReactNode;
}

/**
 * Striped label/value rows. Long field lists are easier to scan with a fixed
 * label column and visible row separation than as a free-flowing grid.
 */
export function FieldRows({ rows }: { rows: FieldRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border">
      {rows.map((row, index) => (
        <div
          key={row.label}
          className={`grid grid-cols-1 gap-1 px-4 py-2.5 sm:grid-cols-[minmax(120px,220px)_1fr] sm:gap-4 ${
            index > 0 ? 'border-t' : ''
          } ${index % 2 === 1 ? 'bg-muted/30' : ''}`}
        >
          <div className="text-sm text-muted-foreground">{row.label}</div>
          <div className="whitespace-pre-wrap break-words text-sm">{row.value}</div>
        </div>
      ))}
    </div>
  );
}

/** Simple label/value pairs for a compact contact block. */
export function DetailList({ rows }: { rows: FieldRow[] }) {
  const visible = rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== '');
  if (visible.length === 0) return null;
  return (
    <dl className="space-y-2">
      {visible.map((row) => (
        <div key={row.label} className="grid grid-cols-[88px_1fr] items-baseline gap-3">
          <dt className="text-sm text-muted-foreground">{row.label}</dt>
          <dd className="min-w-0 break-words text-sm">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ---------------------------------------------------------------------- links */

/**
 * Contact values are actions, not text. A phone number on a lead should dial,
 * and an email should compose — on a phone especially, where these lists are
 * most often read.
 */
export function PhoneLink({ value, showIcon }: { value?: string; showIcon?: boolean }) {
  if (!value) return null;
  return (
    <a className="inline-flex items-center gap-1 font-medium text-primary hover:underline" href={`tel:${value.replace(/[^\d+]/g, '')}`}>
      {showIcon && <Phone className="h-3.5 w-3.5" />}
      {value}
    </a>
  );
}

export function EmailLink({ value, showIcon }: { value?: string; showIcon?: boolean }) {
  if (!value) return null;
  return (
    <a className="inline-flex items-center gap-1 break-all font-medium text-primary hover:underline" href={`mailto:${value}`}>
      {showIcon && <Mail className="h-3.5 w-3.5" />}
      {value}
    </a>
  );
}

export function UrlLink({ value, label }: { value?: string; label?: string }) {
  if (!value) return null;
  return (
    <a
      className="inline-flex items-center gap-1 break-all font-medium text-primary hover:underline"
      href={value}
      target="_blank"
      rel="noreferrer"
    >
      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
      {label ?? value}
    </a>
  );
}

/* ----------------------------------------------------------------- stat tiles */

/**
 * Headline counts above a list. Answers "is anything happening?" before the
 * reader parses a single row.
 */
export function StatTiles({
  stats,
}: {
  stats: Array<{ label: string; value: ReactNode; icon?: ReactNode }>;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {stats.map((stat) => (
        <div key={stat.label} className="rounded-lg border bg-card p-4">
          <div className="flex items-start justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {stat.label}
            </span>
            {stat.icon && <span className="text-muted-foreground">{stat.icon}</span>}
          </div>
          <div className="mt-2 text-3xl font-semibold tabular-nums">{stat.value}</div>
        </div>
      ))}
    </div>
  );
}

/** Quiet framed block for generated prose, so it reads as machine-written. */
export function CalloutBlock({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4 text-sm leading-relaxed">{children}</div>
  );
}

export function formatDateLabel(iso?: string): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTimeLabel(iso?: string): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}
